import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, ExternalLink, Pencil } from 'lucide-react';
import { message } from 'antd';
import { V, cardStyle, cardHoverStyle, h2Style, secondaryBtnStyle } from '../vividGlass';
import brandDnaActions from '../../../../actions/brandDna';

const textBtn = (color) => ({
  background: 'none', border: 'none', color, fontSize: 12,
  fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0',
});

function CompetitorRow({ competitor, brandId, isOwner, onChanged }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    competitor_name: competitor.competitor_name || '',
    competitor_url: competitor.competitor_url || '',
    competitor_positioning: competitor.competitor_positioning || '',
  });

  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 8, boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.07)', border: `1px solid rgba(255,255,255,0.18)`,
    color: V.text, fontSize: 13, outline: 'none', fontFamily: V.font,
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await brandDnaActions.deleteCompetitor(brandId, competitor.brand_competitor_id);
      message.success('Competitor removed');
      onChanged();
    } catch {
      message.error('Failed to remove competitor');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleSave = async () => {
    if (!form.competitor_name.trim()) {
      message.warning('Name is required');
      return;
    }
    setSaving(true);
    try {
      await brandDnaActions.updateCompetitor(brandId, competitor.brand_competitor_id, form);
      message.success('Competitor updated');
      setEditing(false);
      onChanged();
    } catch {
      message.error('Failed to update competitor');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setForm({
      competitor_name: competitor.competitor_name || '',
      competitor_url: competitor.competitor_url || '',
      competitor_positioning: competitor.competitor_positioning || '',
    });
    setEditing(false);
  };

  return (
    <div
      style={{
        padding: editing ? '14px' : '12px 14px', borderRadius: 14,
        background: editing ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${editing ? 'rgba(255,255,255,0.18)' : V.glassBorder}`,
        marginBottom: 8, transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      {editing ? (
        /* Edit form */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            autoFocus
            value={form.competitor_name}
            onChange={(e) => setForm((p) => ({ ...p, competitor_name: e.target.value }))}
            placeholder="Competitor name *"
            style={inputStyle}
          />
          <input
            value={form.competitor_url}
            onChange={(e) => setForm((p) => ({ ...p, competitor_url: e.target.value }))}
            placeholder="Website URL (optional)"
            style={inputStyle}
          />
          <textarea
            value={form.competitor_positioning}
            onChange={(e) => setForm((p) => ({ ...p, competitor_positioning: e.target.value }))}
            placeholder="Positioning / notes (optional)"
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={handleSave} disabled={saving}
              style={{ padding: '7px 16px', borderRadius: 10, background: 'linear-gradient(90deg,#FF7E67,#FF4D8D)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: V.font, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={cancelEdit}
              style={{ padding: '7px 14px', borderRadius: 10, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 13, cursor: 'pointer', fontFamily: V.font }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        /* Display row */
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: competitor.competitor_positioning ? 4 : 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: V.text, fontFamily: V.font }}>{competitor.competitor_name}</span>
              {competitor.competitor_url && (
                <a href={competitor.competitor_url} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'flex', alignItems: 'center', color: V.textMuted, textDecoration: 'none' }}>
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
            {competitor.competitor_positioning && (
              <p style={{ fontSize: 13, color: V.textSub, margin: 0, lineHeight: 1.5, fontFamily: V.font }}>
                {competitor.competitor_positioning}
              </p>
            )}
          </div>

          {isOwner && (
            <div style={{ display: 'flex', gap: 12, flexShrink: 0, alignItems: 'center' }}>
              {confirmDelete ? (
                <>
                  <button type="button" onClick={handleDelete} disabled={deleting} style={textBtn('#f5576c')}>
                    {deleting ? 'Removing…' : 'Remove'}
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(false)} style={textBtn(V.textMuted)}>
                    Keep
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => setEditing(true)}
                    style={{ background: 'transparent', border: 'none', color: V.textMuted, cursor: 'pointer', padding: 4 }}>
                    <Pencil size={12} />
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(true)}
                    style={{ background: 'transparent', border: 'none', color: V.textMuted, cursor: 'pointer', padding: 4 }}>
                    <Trash2 size={13} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CompetitorsCard({ brand, brandId, onRefresh }) {
  const competitors = brand.competitors || [];
  const isOwner = brand.is_owner;
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [form, setForm] = useState({ competitor_name: '', competitor_url: '', competitor_positioning: '' });

  const handleAdd = async () => {
    if (!form.competitor_name.trim()) {
      message.warning('Competitor name is required');
      return;
    }
    setSaving(true);
    try {
      await brandDnaActions.createCompetitor(brandId, form);
      message.success('Competitor added');
      setForm({ competitor_name: '', competitor_url: '', competitor_positioning: '' });
      setShowForm(false);
      onRefresh();
    } catch {
      message.error('Failed to add competitor');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 10, boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.06)', border: `1px solid ${V.glassBorder}`,
    color: V.text, fontSize: 14, outline: 'none', fontFamily: V.font,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...cardStyle, ...(hovered ? cardHoverStyle : {}), marginBottom: 24 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid rgba(255,255,255,0.07)` }}>
        <div>
          <h2 style={{ ...h2Style, marginBottom: 2 }}>Competitors</h2>
          <span style={{ fontSize: 11, color: V.textMuted, fontFamily: V.font }}>
            {competitors.length} {competitors.length === 1 ? 'competitor' : 'competitors'}
          </span>
        </div>
        {isOwner && (
          <button type="button"
            onClick={() => setShowForm((v) => !v)}
            style={{ ...secondaryBtnStyle, padding: '7px 14px', fontSize: 12 }}
          >
            <Plus size={13} /> Add Competitor
          </button>
        )}
      </div>

      {/* Add form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ overflow: 'hidden', marginBottom: 16 }}
          >
            <div style={{ padding: 16, borderRadius: 14, background: 'rgba(255,126,103,0.06)', border: '1px solid rgba(255,126,103,0.2)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                <input value={form.competitor_name} onChange={(e) => setForm((p) => ({ ...p, competitor_name: e.target.value }))}
                  placeholder="Competitor name *" style={inputStyle} />
                <input value={form.competitor_url} onChange={(e) => setForm((p) => ({ ...p, competitor_url: e.target.value }))}
                  placeholder="Website URL (optional)" style={inputStyle} />
                <textarea value={form.competitor_positioning} onChange={(e) => setForm((p) => ({ ...p, competitor_positioning: e.target.value }))}
                  placeholder="Positioning / notes (optional)" rows={2}
                  style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={handleAdd} disabled={saving}
                  style={{ padding: '9px 18px', borderRadius: 12, background: 'linear-gradient(90deg,#FF7E67,#FF4D8D)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: V.font, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ padding: '9px 18px', borderRadius: 12, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 14, cursor: 'pointer', fontFamily: V.font }}>
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Competitor list */}
      {competitors.length === 0 ? (
        <p style={{ color: V.textSub, fontSize: 14, textAlign: 'center', padding: '20px 0', fontFamily: V.font }}>
          No competitors added yet
        </p>
      ) : (
        <AnimatePresence>
          {competitors.map((c) => (
            <CompetitorRow
              key={c.brand_competitor_id}
              competitor={c}
              brandId={brandId}
              isOwner={isOwner}
              onChanged={onRefresh}
            />
          ))}
        </AnimatePresence>
      )}
    </motion.div>
  );
}
